import Seo from "@/components/Seo";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, ArrowLeft, ArrowRight, X } from "lucide-react";
import { CASE_STUDIES, CaseStudySection } from "@/lib/caseStudies";
import BottomCTA from "@/components/BottomCTA";

export default function CaseStudyDetail() {
  const { slug } = useParams();
  const idx = CASE_STUDIES.findIndex((x) => x.slug === slug);
  const cs = idx >= 0 ? CASE_STUDIES[idx] : null;
  const prev = idx > 0 ? CASE_STUDIES[idx - 1] : null;
  const next =
    idx >= 0 && idx < CASE_STUDIES.length - 1
      ? CASE_STUDIES[idx + 1]
      : null;
  const hasSiblings = CASE_STUDIES.length > 1;
  const [filmOpen, setFilmOpen] = useState(false);

  useEffect(() => {
    setFilmOpen(false);
  }, [slug]);

  useEffect(() => {
    if (filmOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [filmOpen]);

  useEffect(() => {
    if (!filmOpen) return;
    const onKey = (e) => e.key === "Escape" && setFilmOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [filmOpen]);

  if (!cs) {
    return (
      <main className="min-h-screen flex items-center justify-center px-6 bg-white">
        <div className="text-center">
          <p className="label-eyebrow text-accent-blue">404</p>
          <h1 className="font-heading font-black text-4xl mt-4 text-slate-900">
            Case study not found.
          </h1>
          <Link
            to="/case-studies"
            data-testid="case-study-not-found-back"
            className="mt-6 inline-flex items-center gap-2 text-accent-blue text-sm font-bold uppercase tracking-widest"
          >
            <ArrowLeft className="h-4 w-4" /> All case studies
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main
      data-testid={`case-study-detail-${cs.slug}`}
      className="bg-white relative"
    >
      <Seo
        title={`${cs.client} case study${cs.title ? " - " + cs.title : ""}`}
        description={`${cs.client} case study on HighwaysIndustry.com. ${cs.intro || cs.tagline || "A UK highways and infrastructure campaign."}`}
        path={`/case-studies/${cs.slug}`}
        image={cs.hero || undefined}
        type="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": cs.title || `${cs.client} case study`,
          "description": cs.intro || cs.tagline || "",
          "image": cs.hero ? `https://whyhighwaysindustry.com${cs.hero}` : "https://whyhighwaysindustry.com/og-image.jpg",
          "author": { "@type": "Organization", "name": "HighwaysIndustry.com" },
          "publisher": { "@type": "Organization", "name": "HighwaysIndustry.com", "logo": { "@type": "ImageObject", "url": "https://whyhighwaysindustry.com/highways-industry-mark.png" } },
          "about": { "@type": "Organization", "name": cs.client },
          "mainEntityOfPage": `https://whyhighwaysindustry.com/case-studies/${cs.slug}`,
        }}
      />
      {/* Floating "Back" pill, top-left over the hero */}
      <div className="absolute top-24 md:top-28 left-5 md:left-10 z-20">
        <Link
          to="/case-studies"
          data-testid="case-study-back"
          className="group inline-flex items-center gap-2 bg-black/60 backdrop-blur-md text-white border border-white/20 hover:border-accent-blue px-4 py-2 text-xs font-bold uppercase tracking-widest rounded-full transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5 transition-transform group-hover:-translate-x-0.5" />
          All Case Studies
        </Link>
      </div>

      <CaseStudySection
        key={cs.slug}
        cs={cs}
        onWatchFilm={() => setFilmOpen(true)}
      />

      {/* ─────────────── Prev / Next case study ─────────────── */}
      {hasSiblings && (
        <section
          data-testid="case-study-pager"
          className="border-t border-slate-200 bg-white"
        >
          <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-12 md:py-16 grid md:grid-cols-2 gap-4 md:gap-6">
            {prev ? (
              <Link
                to={`/case-studies/${prev.slug}`}
                data-testid="case-study-prev"
                className="group relative flex items-center gap-5 p-5 md:p-6 border border-slate-200 hover:border-accent-blue rounded-2xl bg-white transition-colors"
              >
                <span className="inline-flex items-center justify-center h-12 w-12 md:h-14 md:w-14 rounded-full border border-slate-300 group-hover:bg-accent-blue group-hover:border-accent-blue group-hover:text-white text-slate-700 transition-colors shrink-0">
                  <ArrowLeft className="h-5 w-5 transition-transform group-hover:-translate-x-0.5" />
                </span>
                <div className="min-w-0">
                  <p className="font-mono text-[10px] tracking-widest text-slate-500 uppercase">
                    Previous Case Study
                  </p>
                  <p className="font-heading font-bold text-lg md:text-xl mt-1 text-slate-900 leading-tight truncate">
                    {prev.cardTitle}
                  </p>
                </div>
                {prev.thumb && (
                  <img
                    src={prev.thumb}
                    alt=""
                    aria-hidden
                    className="hidden md:block absolute right-5 top-1/2 -translate-y-1/2 h-14 w-20 object-cover rounded-md opacity-70 group-hover:opacity-100 transition-opacity"
                  />
                )}
              </Link>
            ) : (
              <div />
            )}

            {next ? (
              <Link
                to={`/case-studies/${next.slug}`}
                data-testid="case-study-next"
                className="group relative flex items-center gap-5 p-5 md:p-6 border border-slate-200 hover:border-accent-blue rounded-2xl bg-white transition-colors md:flex-row-reverse text-right"
              >
                <span className="inline-flex items-center justify-center h-12 w-12 md:h-14 md:w-14 rounded-full border border-slate-300 group-hover:bg-accent-blue group-hover:border-accent-blue group-hover:text-white text-slate-700 transition-colors shrink-0">
                  <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5" />
                </span>
                <div className="min-w-0">
                  <p className="font-mono text-[10px] tracking-widest text-slate-500 uppercase">
                    Next Case Study
                  </p>
                  <p className="font-heading font-bold text-lg md:text-xl mt-1 text-slate-900 leading-tight truncate">
                    {next.cardTitle}
                  </p>
                </div>
                {next.thumb && (
                  <img
                    src={next.thumb}
                    alt=""
                    aria-hidden
                    className="hidden md:block absolute left-5 top-1/2 -translate-y-1/2 h-14 w-20 object-cover rounded-md opacity-70 group-hover:opacity-100 transition-opacity"
                  />
                )}
              </Link>
            ) : (
              <div />
            )}
          </div>
        </section>
      )}

      {/* Final CTA, per case study */}
      <section
        data-testid="case-study-cta"
        className="dark-section bg-zinc-950 py-24 md:py-32 relative overflow-hidden"
      >
        <div className="absolute inset-0 blueprint-dots opacity-30" />
        <div className="relative mx-auto max-w-[1400px] px-5 md:px-10 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-3">
              <span className="h-px w-12 bg-accent-blue" />
              <span className="label-eyebrow text-accent-blue">
                {cs.cta.eyebrow}
              </span>
            </div>
            <h2 className="font-heading font-black text-4xl md:text-6xl mt-5 leading-[0.95] tracking-tighter text-white">
              {cs.cta.title1}
              <br />
              <span className="text-accent-blue">{cs.cta.title2}</span>
            </h2>
            <p className="mt-8 text-lg text-zinc-300 max-w-2xl leading-relaxed">
              {cs.cta.body}
            </p>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end">
            <Link
              to={cs.cta.buttonLink}
              data-testid="case-study-cta-talk"
              className="group inline-flex items-center gap-3 bg-primary-blue text-white px-8 py-5 text-sm md:text-base font-bold uppercase tracking-widest hover:glow-blue transition-all rounded-full"
            >
              {cs.cta.buttonLabel}
              <ArrowUpRight className="h-5 w-5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Film lightbox */}
      <AnimatePresence>
        {filmOpen && cs.heroVideoFull && (
          <motion.div
            data-testid="case-study-film-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 md:p-10"
            onClick={() => setFilmOpen(false)}
          >
            <button
              type="button"
              onClick={() => setFilmOpen(false)}
              data-testid="case-study-film-close"
              aria-label="Close film"
              className="absolute top-5 right-5 md:top-8 md:right-8 inline-flex items-center justify-center h-12 w-12 rounded-full bg-white text-black hover:bg-accent-blue hover:text-white transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
            <motion.div
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-6xl aspect-video bg-black rounded-2xl overflow-hidden shadow-2xl"
            >
              <video
                key={cs.slug}
                src={cs.heroVideoFull}
                poster={cs.heroPoster}
                controls
                autoPlay
                playsInline
                data-testid="case-study-film-video"
                className="absolute inset-0 w-full h-full object-contain bg-black"
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <BottomCTA
        headline="Ready to make your story next?"
        subline="This is what we do, every day, for the businesses shaping UK highways and infrastructure. Two ways to start."
      />
    </main>
  );
}
